'use client';

import React, { useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { Activity, Eye, EyeOff } from 'lucide-react';
import { useSession } from './SessionProvider';

const CHANNEL_COLORS: Record<string, string> = {
  ch1: '#6ee7b7',
  ch2: '#60a5fa',
  ch3: '#f472b6',
  ch4: '#fb923c',
  ch5: '#a78bfa',
  ch6: '#facc15',
  ch7: '#34d399',
  ch8: '#f87171',
};

const CHANNEL_LABELS: Record<string, string> = {
  ch1: 'Fp1',
  ch2: 'Fp2',
  ch3: 'F3',
  ch4: 'F4',
  ch5: 'C3',
  ch6: 'C4',
  ch7: 'P3',
  ch8: 'P4',
};

const ALL_CHANNELS = ['ch1', 'ch2', 'ch3', 'ch4', 'ch5', 'ch6', 'ch7', 'ch8'];

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ dataKey: string; value: number; color: string }>;
  label?: number;
}

function CustomTooltip({ active, payload }: CustomTooltipProps) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-3 shadow-2xl min-w-[140px]">
      <p className="text-[11px] text-zinc-500 mb-2 font-medium">Amplitude (µV)</p>
      <div className="space-y-1">
        {payload.slice(0, 4).map((entry) => (
          <div key={`tooltip-${entry.dataKey}`} className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
              <span className="text-[11px] text-zinc-400">{CHANNEL_LABELS[entry.dataKey]}</span>
            </div>
            <span className="text-[11px] font-mono tabular-nums text-zinc-200">
              {entry.value.toFixed(1)} µV
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function LiveWaveformChart() {
  const { waveformHistory, status } = useSession();
  const [visibleChannels, setVisibleChannels] = useState<Set<string>>(
    new Set(['ch1', 'ch2', 'ch3', 'ch4'])
  );
  const [showAll, setShowAll] = useState(false);

  const toggleChannel = (ch: string) => {
    setVisibleChannels((prev) => {
      const next = new Set(prev);
      if (next.has(ch)) {
        if (next.size > 1) next.delete(ch);
      } else {
        next.add(ch);
      }
      return next;
    });
  };

  const displayedChannels = showAll ? ALL_CHANNELS : ALL_CHANNELS.slice(0, 4);

  const chartData = waveformHistory.slice(-50).map((pt, i) => ({ ...pt, idx: i }));

  return (
    <div className="rounded-xl border border-zinc-800/60 bg-zinc-900/40 p-3 sm:p-4 h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-zinc-900 border border-zinc-800 flex items-center justify-center flex-shrink-0">
            <Activity size={15} className="text-emerald-400" />
          </div>
          <div className="min-w-0">
            <p className="text-[14px] font-semibold text-zinc-200 truncate">Live EEG Waveform</p>
            <p className="text-[12px] text-zinc-600">
              {status === 'streaming' ? (
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse inline-block" />
                  <span className="hidden sm:inline">Streaming · 256 Hz · {waveformHistory.length} samples</span>
                  <span className="sm:hidden">Live · {waveformHistory.length}pts</span>
                </span>
              ) : status === 'paused' ? (
                'Stream paused'
              ) : (
                'Connecting...'
              )}
            </p>
          </div>
        </div>
        {/* Touch-friendly toggle button — min 44px */}
        <button
          onClick={() => setShowAll((v) => !v)}
          className="flex items-center gap-1.5 px-3 py-2 min-h-[40px] rounded-lg text-[12px] bg-zinc-900 border border-zinc-800 hover:border-zinc-600 text-zinc-400 hover:text-zinc-200 transition-all duration-150 flex-shrink-0 touch-manipulation active:scale-95"
        >
          {showAll ? <EyeOff size={13} /> : <Eye size={13} />}
          {showAll ? '4-ch' : 'All 8'}
        </button>
      </div>

      {/* Channel toggles — larger tap targets on mobile */}
      <div className="flex items-center gap-2 mb-3 flex-wrap overflow-x-auto pb-1 scrollbar-none">
        {displayedChannels.map((ch) => (
          <button
            key={`ch-toggle-${ch}`}
            onClick={() => toggleChannel(ch)}
            className={`flex items-center gap-1.5 px-3 py-1.5 min-h-[36px] rounded-full text-[12px] border transition-all duration-150 flex-shrink-0 touch-manipulation active:scale-95 ${
              visibleChannels.has(ch)
                ? 'border-transparent text-zinc-900 font-semibold' :'border-zinc-800 text-zinc-500 bg-transparent hover:border-zinc-600'
            }`}
            style={
              visibleChannels.has(ch)
                ? { backgroundColor: CHANNEL_COLORS[ch] }
                : {}
            }
          >
            {CHANNEL_LABELS[ch]}
          </button>
        ))}
      </div>

      {/* Chart — taller on tablet for waveform clarity */}
      <div className="h-44 sm:h-52 md:h-60 lg:h-56">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
            <CartesianGrid stroke="rgba(255,255,255,0.04)" strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="idx" tick={false} axisLine={false} tickLine={false} />
            <YAxis
              domain={[-40, 40]}
              tick={{ fontSize: 11, fill: '#52525b', fontFamily: 'IBM Plex Mono' }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(v) => `${v}`}
            />
            <Tooltip content={<CustomTooltip />} />
            {ALL_CHANNELS.filter((ch) => visibleChannels.has(ch)).map((ch) => (
              <Line
                key={`waveform-line-${ch}`}
                type="monotone"
                dataKey={ch}
                stroke={CHANNEL_COLORS[ch]}
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
                strokeOpacity={0.95}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Legend row — scrollable on mobile */}
      <div className="flex items-center gap-3 mt-2 overflow-x-auto pb-1 scrollbar-none">
        {displayedChannels.filter((ch) => visibleChannels.has(ch)).map((ch) => (
          <div key={`legend-${ch}`} className="flex items-center gap-1.5 flex-shrink-0">
            <span className="w-4 h-0.5 rounded-full inline-block" style={{ backgroundColor: CHANNEL_COLORS[ch] }} />
            <span className="text-[12px] font-mono text-zinc-500">{CHANNEL_LABELS[ch]}</span>
          </div>
        ))}
        <span className="text-[11px] text-zinc-700 ml-auto flex-shrink-0 hidden sm:inline">Amplitude (µV) · Bandpass 0.5–45 Hz</span>
      </div>
    </div>
  );
}